<script setup lang="ts">
const dialog = defineModel<boolean>()
const emit = defineEmits(['stateChanged'])

const createDialog = ref<boolean>(false)

const { listChildren, updateChildSettings } = useApi()

const headers = [
  { title: 'ユーザー名', value: 'username', key: 'username' },
  { title: '冒険者名', value: 'name', key: 'name' },
  // 保護者の設定（#944）。羽根筆（AI が魔導書を書く）を使わせるか。既定 OFF
  { title: '羽根筆', value: 'quill', key: 'quill' },
  // 作品の公開（クリスタル化）を許すか（#975）。既定 OFF
  { title: '公開', value: 'crystallize', key: 'crystallize' },
]

// 羽根筆を ON にするときは同意の画面を挟む。OFF はすぐ
const consentFor = ref<ChildInfo | null>(null)
const consentDialog = ref(false)
const saving = ref(false)

const onToggleQuill = async (child: ChildInfo, on: boolean | null) => {
  if (on) {
    consentFor.value = child
    consentDialog.value = true
    return
  }
  await saveQuill(child, false)
}

// 作品の公開も、ON にするときは同意の画面を挟む（#975）。OFF はすぐ
const publishConsentFor = ref<ChildInfo | null>(null)
const publishConsentDialog = ref(false)

const onTogglePublish = async (child: ChildInfo, on: boolean | null) => {
  if (on) {
    publishConsentFor.value = child
    publishConsentDialog.value = true
    return
  }
  await savePublish(child, false)
}

const savePublish = async (child: ChildInfo, crystallize: boolean) => {
  saving.value = true
  try {
    const res = await updateChildSettings(child.userId, { crystallize, consent: crystallize })
    child.crystallize = res.crystallize
    publishConsentDialog.value = false
  }
  catch (e) {
    console.error('[ListChildrenDialog] failed to update settings', e)
    alert('設定を変えられませんでした。時間をおいてもう一度お試しください。')
  }
  finally {
    saving.value = false
  }
}

const saveQuill = async (child: ChildInfo, quill: boolean) => {
  saving.value = true
  try {
    const res = await updateChildSettings(child.userId, { quill, consent: quill })
    child.quill = res.quill
    consentDialog.value = false
  }
  catch (e) {
    console.error('[ListChildrenDialog] failed to update settings', e)
    alert('設定を変えられませんでした。時間をおいてもう一度お試しください。')
  }
  finally {
    saving.value = false
  }
}

const childAccounts = ref<ChildInfo[]>([])

const noMoreChildren = computed<boolean>(() => {
  return childAccounts.value.length >= 3
})

const onCreate = () => {
  createDialog.value = true
}

const onStateChanged = async () => {
  childAccounts.value = await listChildren()

  emit('stateChanged')
}

// onMounted(async () => {
//   childAccounts.value = await listChildren()
// })

onBeforeUpdate(async () => {
  childAccounts.value = await listChildren()
})
</script>

<template>
  <Dialog
    v-model="dialog"
    title="子アカウント"
    :cancel-button="{ title: 'とじる' }"
  >
    <template #default>
      <v-container class="align-center">
        <v-data-table
          :headers="headers"
          :items="childAccounts"
          item-key="key"
          items-per-page="3"
          hide-default-footer
          disable-sort
          :sort-by="[{ key: 'order', order: 'asc' }]"
          class="list-of-areas-table"
        >
          <template #[`item.userId`]="{ item }">
            {{ item.userId }}
          </template>

          <template #[`item.quill`]="{ item }">
            <v-switch
              :model-value="item.quill"
              :disabled="saving"
              color="primary"
              hide-details
              density="compact"
              inset
              :data-testid="`child-quill-${item.username}`"
              @update:model-value="onToggleQuill(item, $event)"
            />
          </template>

          <template #[`item.crystallize`]="{ item }">
            <v-switch
              :model-value="item.crystallize"
              :disabled="saving"
              color="primary"
              hide-details
              density="compact"
              inset
              :data-testid="`child-crystallize-${item.username}`"
              @update:model-value="onTogglePublish(item, $event)"
            />
          </template>

          <template #no-data>
            子アカウントはありません。
          </template>
        </v-data-table>

        <v-row class="justify-center mt-10">
          <v-btn
            v-if="!noMoreChildren"
            variant="elevated"
            size="xlarge"
            color="primary"
            class="pa-3 mx-2"
            @click="onCreate"
          >
            作成する
          </v-btn>
          <template v-else>
            子アカウントはこれ以上作成できません。
          </template>
        </v-row>
      </v-container>

      <dialog-CreateChildDialog
        v-model="createDialog"
        @state-changed="onStateChanged"
      />

      <dialog-GuardianQuillConsentDialog
        v-model="consentDialog"
        :child-name="consentFor?.name ?? ''"
        :busy="saving"
        @agreed="consentFor && saveQuill(consentFor, true)"
      />

      <dialog-GuardianCrystallizeConsentDialog
        v-model="publishConsentDialog"
        :child-name="publishConsentFor?.name ?? ''"
        :busy="saving"
        @agreed="publishConsentFor && savePublish(publishConsentFor, true)"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.list-of-areas-table {
  font-size: 1.3rem;
  background-color: rgb(255, 255, 255, 0.7);
}

.list-of-areas-table th {
  background-color: rgb(255, 255, 255, 0.8) !important;
}
</style>
